import { useCallback, useEffect, useState } from 'react'
import { Grid, IconButton, FormGroup, FormControlLabel, Checkbox, Button, useMediaQuery } from '@mui/material'
import { DateRange } from '@mui/x-date-pickers-pro'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'

import { useSelector } from 'react-redux'
import { setDate } from 'date-fns'
import actionsService from '../../../services/actionsService'
import { Employee, SelectOption, Team } from '../../../services/dashboardService'
import AutocompleteList from '../../../shared/UI/AutocompleteList'
import ButtonSmall from '../../../shared/UI/ButtonSmall'
import { IOSStyleSwitch } from '../../../shared/UI/IOSStyleSwitch/IOSStyleSwitch'
import Paragraph from '../../../shared/UI/Paragraph'
import { directReportsStyles } from './Styles'
import { MyActionsFilterProps, MyActionsFilterState } from './Types'
import DateRangePicker from '../../../shared/UI/DateRangePicker'
import { RootStore, useAppDispatch } from '../../../redux/store'
import DropdownMenu from '../../../shared/UI/DropdownMenu'
import theme from '../../../theme/theme'
import { MOBILE_EXCLUDED_REQUEST_TYPES } from '../../MyActionsGrid/Shared/enums'
import {
    resetFilters,
    setFilterStates,
    setDateFrom,
    setDateTo,
} from '../../../redux/reducers/myActionsReducer'

const testId = 'my-actions-filter'
function FilterPanel({
    selectionLists,
    handleClose,
}: MyActionsFilterProps & { handleClose: () => void }) {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { filterStates } = useSelector((state: RootStore) => state.myActions)
  const dispatch = useAppDispatch()
  const {
    allEmployees,
    secondaryApproverEmployees,
    secondaryApproverDepartments,
    secondaryApproverTeams,
    leaverEmployees,
  } = useSelector((state: RootStore) => state.appSettings)
  const originalDateFrom = useSelector(
    (state: RootStore) => state.myActions.filterStates.requestParams.dateFrom,
  )
  const originalDateTo = useSelector(
    (state: RootStore) => state.myActions.filterStates.requestParams.dateTo
  )

    /**
     * State is used so that user changes are reflected in the GUI
     * requestFilterSettings are updated with this call but does not tigger a rerender.
     * dispatch is called when filters are applied
     */
    const [filterState, setFilterState] = useState<MyActionsFilterState>(filterStates)
    const [teamOptions, setTeamOptions] = useState<Team[]>([])
    const [employeeOptions, setEmployeeOptions] = useState<Employee[]>([])
    const [departmentOptions, setDepartmentOptions] = useState<SelectOption[]>([])
    const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)

    const [teamSelectionDisabled, setTeamSelectionDisabled] = useState<boolean>(
        selectionLists.current.departments.length > 1
    )

    // filterState.checkboxSettings is a local snapshot (see comment above) so that
    // user changes aren't dispatched until "Apply" is clicked. However, on mobile
    // the drawer is kept mounted (MUI SwipeableDrawer keepMounted), so this panel
    // can mount before the request counts have been fetched (e.g. counts are
    // undefined immediately after "Approvals Pending" navigates here). Sync the
    // counts in from redux as they arrive, without discarding the user's checked
    // selections.
    useEffect(() => {
        setFilterState(latestFilterState => ({
            ...latestFilterState,
            checkboxSettings: latestFilterState.checkboxSettings.map(setting => {
                const updatedSetting = filterStates.checkboxSettings.find(s => s.type === setting.type)
                return updatedSetting ? { ...setting, count: updatedSetting.count } : setting
            }),
        }))
    }, [filterStates.checkboxSettings])

    useEffect(() => {
        const combinedDepartments = [...selectionLists.current.departments]
        if (!filterState.selectionState.directReportees) {
            secondaryApproverDepartments.forEach(secDep => {
                if (!combinedDepartments.some(dep => dep.value === secDep.value)) {
                    combinedDepartments.push(secDep)
                }
            })
        }

        if (combinedDepartments.length > 1 && !combinedDepartments.some(d => d.value === 0)) {
            combinedDepartments.unshift({ displayValue: 'All', value: 0 })
        }

        setDepartmentOptions(combinedDepartments)
    }, [secondaryApproverDepartments, selectionLists, filterState.selectionState.directReportees])

    useEffect(() => {
        const { directReportees, department, team } = filterState.selectionState

    const filteredEmployees = allEmployees.filter(emp => {
      let res = true
      if (directReportees && !emp.directReportee) res = false
      if (!directReportees && !emp.inHierarchy) res = false
      if (
        department &&
        department.displayValue !== 'All' &&
        emp.departmentId !== department.value
      )
        res = false
      if (team && emp.teamId !== team.teamId) res = false
      return res
    })
    let combinedEmployees = [...filteredEmployees]
    if (!directReportees) {
      secondaryApproverEmployees.forEach(secEmp => {
        if (team && secEmp.teamId !== team.teamId) {
          return
        }
        if (
          department &&
          department.displayValue !== 'All' &&
          secEmp.departmentId !== department.value
        ) {
          return
        }
        if (!combinedEmployees.some(emp => emp.employeeId === secEmp.employeeId)) {
          combinedEmployees.push(secEmp)
        }
      })
    }

    leaverEmployees.forEach(leaverEmp => {
        if (team && leaverEmp.teamId !== team.teamId){
            return
        }
        if (
            department &&
            department.displayValue !== 'All' &&
            leaverEmp.departmentId !== department.value
        ) {
            return
        }
        if (directReportees && !leaverEmp.directReportee) {
            return
        }

        if (!combinedEmployees.some(emp => emp.employeeId === leaverEmp.employeeId)) {
            combinedEmployees.push(leaverEmp)
        }
    })

    combinedEmployees = combinedEmployees.sort((a, b) => (a.employeeName.toLowerCase() > b.employeeName.toLowerCase() ? 0 : -1))
    setEmployeeOptions(combinedEmployees)
  }, [allEmployees, secondaryApproverEmployees, filterState.selectionState, leaverEmployees])

    const initialiseTeamOptions = useEffect(() => {
        const { departments, teams } = selectionLists.current

        const selectedDepartment = filterState.selectionState.department || departments[0]
        let options: Team[] = []

        if (
            selectedDepartment &&
            selectedDepartment.value !== 0
        ) {
            options = teams[selectedDepartment.value] ? [...teams[selectedDepartment.value]] : []

            if (!filterState.selectionState.directReportees) {
                secondaryApproverTeams.forEach(secTeam => {
                    if (secTeam.departmentId === selectedDepartment.value) {
                        if (!options.some(t => t.teamId === secTeam.teamId)) {
                            options.push(secTeam)
                        }
                    }
                })
            }
        }

        setTeamSelectionDisabled(options.length === 0)
        setTeamOptions(options)
    }, [])

    const updateTeamOptions = useCallback(
        (departmentOption?: SelectOption) => {
            const { teams } = selectionLists.current
            let options: Team[] = []

            if (
                departmentOption &&
                departmentOption.value !== 0
            ) {
                options = teams[departmentOption.value] ? [...teams[departmentOption.value]] : []

                if (!filterState.selectionState.directReportees) {
                    secondaryApproverTeams.forEach(secTeam => {
                        if (secTeam.departmentId === departmentOption.value) {
                            if (!options.some(t => t.teamId === secTeam.teamId)) {
                                options.push(secTeam)
                            }
                        }
                    })
                }
            }

            setTeamSelectionDisabled(options.length === 0)
            setTeamOptions(options)
        }, [])

    /**
     * Filter values are reset and the GUI refresh triggerd in the parent page
     */
    const resetToDefaultSettings = useCallback(() => {
        actionsService.userAnalytics
            .postEvent('UIClickEvent', 'MyActionsFiltersResetAllClick', window.location.pathname, window.location.hostname)
            .catch((error: unknown) => console.error('Analytics tracking error:', error))
        dispatch(resetFilters())
        handleClose()
    }, [])

    const handleCheckBoxChange = useCallback((type: string) => {
        actionsService.userAnalytics
            .postEvent('UIClickEvent', `MyActionsFiltersTypeCheckbox${type}Click`, window.location.pathname, window.location.hostname)
            .catch((error: unknown) => console.error('Analytics tracking error:', error))
        setFilterState(latestFilterState => ({
            ...latestFilterState,
            checkboxSettings: latestFilterState.checkboxSettings.map(setting => ({
                ...setting,
                checked: setting.type === type ? !setting.checked : setting.checked,
            })),
        }))
    }, [])

    const handleStatusSelection = useCallback((value: number) => {
        actionsService.userAnalytics
            .postEvent('UIClickEvent', 'MyActionsFiltersStatusDropdownChange', window.location.pathname, window.location.hostname)
            .catch((error: unknown) => console.error('Analytics tracking error:', error))
        setFilterState(latestFilterState => ({
            ...latestFilterState,
            requestParams: { ...latestFilterState.requestParams, requestStatusId: value },
            selectionState: { ...latestFilterState.selectionState, status: value },
        }))
    }, [])

    const handleEmployeeSelection = useCallback((employees: Employee[] | null) => {
        actionsService.userAnalytics
            .postEvent('UIClickEvent', 'MyActionsFiltersEmployeeDropdownChange', window.location.pathname, window.location.hostname)
            .catch((error: unknown) => console.error('Analytics tracking error:', error))
        setFilterState(latestFilterState => ({
            ...latestFilterState,
            requestParams: {
                ...latestFilterState.requestParams,
                employeeIds: employees?.map(emp => emp.employeeId),
            },
            selectionState: { ...latestFilterState.selectionState, employees },
        }))
    }, [])

    const handleTeamSelection = useCallback(
        (team: Team | null) => {
            actionsService.userAnalytics
                .postEvent('UIClickEvent', 'MyActionsFiltersTeamDropdownChange', window.location.pathname, window.location.hostname)
                .catch((error: unknown) => console.error('Analytics tracking error:', error))
            const employees = team
                ? filterState.selectionState.employees?.filter(emp => {
                    if (team && team.teamId !== emp.teamId) return false
                    return true
                })
                : filterState.selectionState.employees
            const employeeIds = employees?.map(emp => emp.employeeId)

            setFilterState(latestFilterState => ({
                ...latestFilterState,
                requestParams: {
                    ...latestFilterState.requestParams,
                    departmentTeamId: team ? team.teamId : undefined,
                    employeeIds,
                },
                selectionState: {
                    ...latestFilterState.selectionState,
                    team,
                    employees: employees || [],
                },
            }))
        },
        [filterState.selectionState.employees]
    )

    const handleDepartmentSelection = useCallback(
        (departmentOption: SelectOption | null) => {
            actionsService.userAnalytics
                .postEvent('UIClickEvent', 'MyActionsFiltersDepartmentDropdownChange', window.location.pathname, window.location.hostname)
                .catch((error: unknown) => console.error('Analytics tracking error:', error))
            /*
             * if departmentOption is null or 0:
             * disable team selection
             * remove current team selection
             */
            handleTeamSelection(null)
            if (!departmentOption || departmentOption.value === 0) {
                setTeamSelectionDisabled(true)
            } else {
                updateTeamOptions(departmentOption)
            }
            const employees = departmentOption
                ? filterState.selectionState.employees?.filter(emp => {
                    if (departmentOption && departmentOption.value !== emp.departmentId) return false
                    return true
                })
                : filterState.selectionState.employees
            const employeeIds = employees?.map(emp => emp.employeeId)

            setFilterState(latestFilterState => ({
                ...latestFilterState,
                requestParams: {
                    ...latestFilterState.requestParams,
                    departmentId: departmentOption ? departmentOption.value : undefined,
                    employeeIds,
                },
                selectionState: {
                    ...latestFilterState.selectionState,
                    department: departmentOption,
                    employees: employees || [],
                },
            }))
        },
        [filterState.selectionState.employees, handleTeamSelection, updateTeamOptions]
    )

    const handleDateRangeSelection = useCallback((dateRange: DateRange<Date>) => {
        actionsService.userAnalytics
            .postEvent('UIClickEvent', 'MyActionsFiltersDateRangeChange', window.location.pathname, window.location.hostname)
            .catch((error: unknown) => console.error('Analytics tracking error:', error))
        const [dateFrom, dateTo] = dateRange
        if (dateTo && dateFrom) {
            setFilterState(latestFilterState => ({
                ...latestFilterState,
                requestParams: { ...latestFilterState.requestParams, dateFrom, dateTo },
                selectionState: { ...latestFilterState.selectionState, dateRange },
            }))
        }
    }, [])

    const determineDatepickerValue = useCallback((dateRange: DateRange<Date>): DateRange<Date> => {
        const [dateFrom, dateTo] = dateRange
        if (dateTo && dateFrom && datePickerOpen) {
            return dateRange
        }
        if (datePickerOpen && !dateTo && !dateFrom) {
            return [null, null]
        }
        return [originalDateFrom, originalDateTo]
    }, [datePickerOpen, originalDateFrom, originalDateTo])

    const handleDatePickerOpen = useCallback(() => {
        const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        const endOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)

        setFilterState(latestFilterState => ({
            ...latestFilterState,
            requestParams: { ...latestFilterState.requestParams, dateFrom: startOfMonth, dateTo: endOfMonth },
            selectionState: { ...latestFilterState.selectionState, dateRange: [null, null] },
        }))
        setDatePickerOpen(true)
    }, [])

    const handleDatePickerClose = useCallback(() => {
        setDatePickerOpen(false)
    }, [])

    const handleDirectReporteesToggle = useCallback(
        (directReportees: boolean) => {
            actionsService.userAnalytics
                .postEvent('UIClickEvent', 'MyActionsFiltersDirectReportsToggleClick', window.location.pathname, window.location.hostname)
                .catch((error: unknown) => console.error('Analytics tracking error:', error))
            const employees = directReportees
                ? filterState.selectionState.employees?.filter(emp => {
                    if (directReportees && !emp.directReportee) return false
                    return true
                })
                : filterState.selectionState.employees
            const employeeIds = employees?.map(emp => emp.employeeId)

            setFilterState(latestFilterState => ({
                ...latestFilterState,
                requestParams: {
                    ...latestFilterState.requestParams,
                    directReportees,
                    employeeIds,
                },
                selectionState: {
                    ...latestFilterState.selectionState,
                    directReportees,
                    employees: employees || [],
                },
            }))
        },
        [filterState.selectionState.employees]
    )

    const submitFilterParams = () => {
        actionsService.userAnalytics
            .postEvent('UIClickEvent', 'MyActionsFiltersApplyClick', window.location.pathname, window.location.hostname)
            .catch((error: unknown) => console.error('Analytics tracking error:', error))
        dispatch(setFilterStates(filterState))
        handleClose()
    }

    return (
        <Grid container data-testid="filter-panel">
            {/* Left side filters */}
            <Grid item xs={12} md={4} p={3} pr={1}>
                <Grid item display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Paragraph weight="bold" style={{ marginLeft: '-8px' }}>
                        Type Filters
                    </Paragraph>
                    <IconButton
                        aria-label="delete"
                        size="large"
                        onClick={handleClose}
                        sx={{ p: 0, display: { sm: 'none' } }}
                    >
                        <CloseOutlinedIcon fontSize="inherit" color="primary" />
                    </IconButton>
                </Grid>
                <FormGroup>
                    <Grid container>
                        {filterState.checkboxSettings
                            .filter(setting => setting?.count && setting?.count > 0)
                            .filter(setting => !isMobile || !MOBILE_EXCLUDED_REQUEST_TYPES.includes(setting.type))
                            .map(({ displayName, type, checked, count }) => (
                                <Grid item xs={4} md={12} gap={1}>
                                    <FormControlLabel
                                        control={<Checkbox checked={checked} defaultChecked={checked} sx={{ mr: 1 }} />}
                                        label={`${displayName} (${count})`}
                                        sx={{ mb: 1 }}
                                        key={type}
                                        data-testid={type}
                                        onChange={() => handleCheckBoxChange(type)}
                                    />
                                </Grid>
                            ))}
                    </Grid>
                </FormGroup>
            </Grid>
            {/* Right side filters */}
            {!filterState.requestParams ? (
                <></>
            ) : (
                <Grid item xs={12} md={8} sx={{ backgroundColor: '#fafafa', p: 3 }}>
                    <Paragraph weight="bold">Filters</Paragraph>
                    <Grid item xs={12} mt={3}>
                        <FormControlLabel
                            control={
                                <IOSStyleSwitch defaultChecked={filterState.requestParams.directReportees} />
                            }
                            label="Direct Reports"
                            labelPlacement="start"
                            onClick={() =>
                                handleDirectReporteesToggle(!filterState.requestParams.directReportees)
                            }
                            data-testid="direct-reports"
                            sx={directReportsStyles}
                        />
                    </Grid>
                    <Grid
                        container
                        display="flex"
                        direction="column"
                        spacing={4}
                        data-testid="my-actions-filter"
                    >
                        <Grid item xs={12}>
                            <Grid container spacing={3} pt={2}>
                                <Grid item xs={12}>
                                    <AutocompleteList
                                        id="department"
                                        label="Department"
                                        data={departmentOptions}
                                        textField="displayValue"
                                        value={filterState.selectionState.department}
                                        onChange={(_e, department: SelectOption) =>
                                            handleDepartmentSelection(department)
                                        }
                                        dataTestId={`${testId}-department-dropdown`}
                                        disabled={departmentOptions.length === 1}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AutocompleteList
                                        id="team"
                                        label="Team"
                                        data={teamOptions}
                                        textField="teamName"
                                        value={filterState.selectionState.team}
                                        onChange={(_e, team: Team) => handleTeamSelection(team)}
                                        dataTestId={`${testId}-team-dropdown`}
                                        disabled={teamSelectionDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AutocompleteList
                                        multiple
                                        id="employee"
                                        label="Employee"
                                        data={employeeOptions}
                                        textField="employeeName"
                                        value={filterState.selectionState.employees || []}
                                        onChange={(_e, employee: Employee[]) => handleEmployeeSelection(employee)}
                                        dataTestId={`${testId}-employee-dropdown`}
                                        limitTags={1}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <DateRangePicker
                                        arrange="Stack"
                                        startText={
                                            filterState.selectionState.dateRange[0]
                                                ? 'Date from'
                                                : `${originalDateFrom.toLocaleDateString(navigator.language)}`
                                        }
                                        endText={
                                            filterState.selectionState.dateRange[0]
                                                ? 'Date to'
                                                : `${originalDateTo.toLocaleDateString(navigator.language)}`
                                        }
                                        onChange={(date: DateRange<Date>) => handleDateRangeSelection(date)}
                                        value={determineDatepickerValue(filterState.selectionState.dateRange)}
                                        dataTestId={`${testId}-date-range`}
                                        fromLabel="Date from"
                                        toLabel="Date to"
                                        onOpen={handleDatePickerOpen}
                                        onClose={handleDatePickerClose}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <DropdownMenu
                                        label="Status"
                                        id="searchStatus"
                                        data={selectionLists.current.statuses}
                                        textField="displayValue"
                                        valueField="value"
                                        value={filterState.selectionState.status}
                                        onChange={e => handleStatusSelection(Number(e.target.value))}
                                        dataTestId="MyActions-Filters-StatusDDL"
                                    />
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    mt={-2}
                                >
                                    <Button
                                        size="small"
                                        onClick={resetToDefaultSettings}
                                        startIcon={<RestartAltIcon />}
                                        sx={{ mr: 4 }}
                                    >
                                        Reset all
                                    </Button>
                                    <ButtonSmall
                                        label="Apply"
                                        sx={{ minWidth: '90px', fontSize: '11px' }}
                                        onClick={e => submitFilterParams()}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Grid>
    )
}

export default FilterPanel

